import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { getContent, seedContent } from './api'
import type { SiteContent } from './content.types'

interface ContentState {
  content: SiteContent
  loading: boolean
  /** true면 API에서 받아온 값, false면 seed 폴백 */
  fromApi: boolean
  reload: () => void
}

const ContentContext = createContext<ContentState | null>(null)

export function ContentProvider({
  children,
  /** Admin 미리보기처럼 콘텐츠를 직접 주입해야 할 때 사용 */
  override,
}: {
  children: ReactNode
  override?: SiteContent
}) {
  const [content, setContent] = useState<SiteContent>(seedContent)
  const [loading, setLoading] = useState(!override)
  const [fromApi, setFromApi] = useState(false)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    if (override) return
    let cancelled = false
    setLoading(true)
    getContent().then((res) => {
      if (cancelled) return
      setContent(res.content)
      setFromApi(res.fromApi)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [override, nonce])

  /**
   * 탭이 다시 보이거나 창에 포커스가 돌아오면 콘텐츠를 다시 불러온다.
   *
   * Admin과 랜딩페이지를 다른 탭에 띄워놓고 작업하는 경우가 많은데,
   * 이게 없으면 Admin에서 저장한 뒤 랜딩 탭으로 돌아와도 처음 불러온 내용이 그대로 남는다.
   * (React는 탭 전환만으로는 다시 요청하지 않는다)
   *
   * 서버가 ETag 를 주므로 내용이 그대로면 304 만 오간다 — 부담이 거의 없다.
   * 짧은 간격으로 여러 번 발생하는 것을 막기 위해 3초 이내 재요청은 건너뛴다.
   */
  useEffect(() => {
    if (override) return

    let last = Date.now()
    const refresh = () => {
      if (document.hidden) return
      const now = Date.now()
      if (now - last < 3000) return
      last = now
      setNonce((n) => n + 1)
    }

    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [override])

  const value = useMemo<ContentState>(
    () => ({
      content: override ?? content,
      loading: override ? false : loading,
      fromApi,
      reload: () => setNonce((n) => n + 1),
    }),
    [override, content, loading, fromApi],
  )

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>
}

export function useContent(): ContentState {
  const ctx = useContext(ContentContext)
  if (!ctx) throw new Error('useContent must be used inside <ContentProvider>')
  return ctx
}
